import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';
import { ServiceRequestItem } from 'services/service_requests.service';

import { isReportOvercharge } from './audit';
import { periodConsumptionKwh } from './invoiceConsumption';

/**
 * Indicadores do gestor de energia (tela de Análises, out/2026 — pedido do
 * Bruno). Funções puras sobre as faturas e UCs que já estão no Redux: nenhuma
 * chamada nova à API além das que a tela faz (solicitações e previsão).
 *
 * Fonte: FATURA (passado/realizado) — regra transversal do projeto. O que é
 * projeção (orçamento × previsão) usa o forecast do backend, que precifica pelo
 * motor/PCAT. Faturas antigas sem algum campo contam como zero nele.
 */

const n = (v: number | null | undefined) =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0;

const byUnit = (invoices: Invoice[], unitId: string) =>
  invoices.filter((inv) => inv.consumerUnit?.id === unitId);

const unitName = (uc: ConsumerUnit) => uc.name || uc.installation;

export const isGroupAUnit = (
  uc: Pick<ConsumerUnit, 'classificationSubgroup'>
) => (uc.classificationSubgroup?.slug ?? '').toLowerCase().startsWith('a');

const prevYearRefMonth = (rm: string) => {
  const [y, m] = rm.split('_');
  return `${Number(y) - 1}_${m}`;
};

// ── 1. Dinheiro perdido e recuperável ─────────────────────────────────────

export interface AvoidableCosts {
  /** Cobrança a mais material apontada pela auditoria (R$). */
  overcharge: number;
  /** Multa, juros e atualização por atraso de pagamento (R$). */
  lateFee: number;
  /** Energia reativa excedente — fator de potência abaixo de 0,92 (R$). */
  reactive: number;
  /** Ultrapassagem de demanda (R$). */
  demandOverrun: number;
  /** Demanda contratada e não utilizada (R$). */
  unusedDemand: number;
  total: number;
}

export const reactiveValue = (inv: Invoice) => {
  const r = inv.reactiveEnergy;
  const fromReactive =
    n(r?.valueReactiveExcessFPWithTax) +
    n(r?.valueReactiveExcessPWithTax) +
    n(r?.valueReactiveExcessIWithTax);
  // Extratores antigos gravavam o reativo só na demanda
  return fromReactive || n(inv.demand?.reactiveEnergyValue);
};

export const overrunValue = (inv: Invoice) =>
  n(inv.demand?.valueDemandOverrunWithTax) +
  n(inv.demand?.valueDemandOverrun_PWithTax);

export const unusedDemandValue = (inv: Invoice) =>
  n(inv.demand?.valueUnusedDemandWithTax) +
  n(inv.demand?.valueUnusedDemand_PWithTax);

export const materialOvercharge = (inv: Invoice) => {
  const s = inv.auditSummary;
  return s &&
    s.status === 'divergent' &&
    isReportOvercharge(s.overcharge, inv.invoiceValue)
    ? n(s.overcharge)
    : 0;
};

export const avoidableCosts = (invoices: Invoice[]): AvoidableCosts => {
  const c = invoices.reduce(
    (acc, inv) => {
      acc.overcharge += materialOvercharge(inv);
      acc.lateFee += n(inv.fees?.lateFee);
      acc.reactive += reactiveValue(inv);
      acc.demandOverrun += overrunValue(inv);
      acc.unusedDemand += unusedDemandValue(inv);
      return acc;
    },
    {
      overcharge: 0,
      lateFee: 0,
      reactive: 0,
      demandOverrun: 0,
      unusedDemand: 0,
    }
  );
  return {
    ...c,
    total:
      c.overcharge + c.lateFee + c.reactive + c.demandOverrun + c.unusedDemand,
  };
};

export type ContestationStatus = 'none' | 'requested' | 'in_progress' | 'done';

export interface OverchargeRow {
  invoiceId: string;
  unitName: string;
  refMonth: string;
  overcharge: number;
  topLabel: string | null;
  contestation: ContestationStatus;
}

/**
 * Faturas com cobrança a mais material e o andamento da contestação
 * ("Reclamação na distribuidora" com o invoiceId no payload).
 */
export const overchargeRows = (
  units: ConsumerUnit[],
  invoices: Invoice[],
  requests: ServiceRequestItem[]
): OverchargeRow[] =>
  invoices
    .filter((inv) => materialOvercharge(inv) > 0)
    .map((inv) => {
      const req = requests.find(
        (r) =>
          r.serviceType === 'reclamacao_distribuidora' &&
          r.status !== 'canceled' &&
          r.payload?.invoiceId === inv.id
      );
      const uc = units.find((u) => u.id === inv.consumerUnit?.id);
      return {
        invoiceId: inv.id,
        unitName: uc ? unitName(uc) : '—',
        refMonth: inv.refMonth,
        overcharge: materialOvercharge(inv),
        topLabel: inv.auditSummary?.topLabel ?? null,
        contestation: (req?.status as ContestationStatus) ?? 'none',
      };
    })
    .sort((a, b) => b.overcharge - a.overcharge);

// ── 2. Uso da demanda contratada (Grupo A) ────────────────────────────────

export interface DemandUsageRow {
  unitId: string;
  unitName: string;
  contractedKW: number;
  /** Média de medida ÷ contratada nos meses com fatura (0–1+). */
  avgUsage: number;
  maxMeasuredKW: number;
  overrunMonths: number;
  months: number;
  unusedDemandCost: number;
  overrunCost: number;
}

export const demandUsage = (
  units: ConsumerUnit[],
  invoices: Invoice[]
): DemandUsageRow[] =>
  units
    .filter(isGroupAUnit)
    .map((uc) => {
      const list = byUnit(invoices, uc.id).filter(
        (inv) => n(inv.demand?.contractedDemandKW) > 0
      );
      if (!list.length) return null;
      const latest = [...list].sort((a, b) =>
        a.refMonth < b.refMonth ? 1 : -1
      )[0];
      const usage = list.map(
        (inv) =>
          n(inv.demand?.measuredDemandKW) / n(inv.demand?.contractedDemandKW)
      );
      return {
        unitId: uc.id,
        unitName: unitName(uc),
        contractedKW: n(latest.demand?.contractedDemandKW),
        avgUsage: usage.reduce((a, b) => a + b, 0) / usage.length,
        maxMeasuredKW: Math.max(
          ...list.map((i) => n(i.demand?.measuredDemandKW))
        ),
        overrunMonths: list.filter((i) => overrunValue(i) > 0).length,
        months: list.length,
        unusedDemandCost: list.reduce((a, i) => a + unusedDemandValue(i), 0),
        overrunCost: list.reduce((a, i) => a + overrunValue(i), 0),
      };
    })
    .filter((r): r is DemandUsageRow => r !== null);

// ── 3. Custo unitário e peso da Ponta ─────────────────────────────────────

export interface UnitCostRow {
  unitId: string;
  unitName: string;
  cost: number;
  kwh: number;
  /** R$/kWh = soma do valor ÷ soma do kWh da distribuidora. */
  costPerKwh: number | null;
  /** Fração do consumo na Ponta (0–1); null para UC sem posto horário. */
  peakShare: number | null;
}

export const unitCosts = (
  units: ConsumerUnit[],
  invoices: Invoice[]
): UnitCostRow[] =>
  units
    .map((uc) => {
      const list = byUnit(invoices, uc.id);
      const cost = list.reduce((a, i) => a + n(i.invoiceValue), 0);
      const kwh = list.reduce((a, i) => a + periodConsumptionKwh(i), 0);
      const peak = list.reduce(
        (a, i) => a + n(i.suppliedEnergy?.suppliedEnergyTE_P),
        0
      );
      return {
        unitId: uc.id,
        unitName: unitName(uc),
        cost,
        kwh,
        costPerKwh: kwh > 0 ? cost / kwh : null,
        peakShare: kwh > 0 && peak > 0 ? peak / kwh : null,
      };
    })
    .filter((r) => r.cost > 0 || r.kwh > 0);

// ── 4. Comparação com o mesmo período do ano anterior ─────────────────────

export interface YearOverYearRow {
  unitId: string;
  unitName: string;
  /** Meses do período que têm fatura nos DOIS anos (a base comparável). */
  months: number;
  kwh: number;
  kwhPrev: number;
  cost: number;
  costPrev: number;
}

/**
 * Só compara mês com mês: entram os meses do período que têm fatura neste
 * ano E no mesmo mês do ano anterior — senão a diferença seria só falta de
 * fatura.
 */
export const yearOverYear = (
  units: ConsumerUnit[],
  invoices: Invoice[],
  months: string[]
): YearOverYearRow[] =>
  units
    .map((uc) => {
      const list = byUnit(invoices, uc.id);
      const row: YearOverYearRow = {
        unitId: uc.id,
        unitName: unitName(uc),
        months: 0,
        kwh: 0,
        kwhPrev: 0,
        cost: 0,
        costPrev: 0,
      };
      months.forEach((rm) => {
        const cur = list.find((i) => i.refMonth === rm);
        const prev = list.find((i) => i.refMonth === prevYearRefMonth(rm));
        if (!cur || !prev) return;
        row.months += 1;
        row.kwh += periodConsumptionKwh(cur);
        row.kwhPrev += periodConsumptionKwh(prev);
        row.cost += n(cur.invoiceValue);
        row.costPrev += n(prev.invoiceValue);
      });
      return row;
    })
    .filter((r) => r.months > 0);

// ── 5. Banco de créditos ──────────────────────────────────────────────────

export interface CreditBankRow {
  unitId: string;
  unitName: string;
  refMonth: string;
  /** Saldo de créditos na última fatura (kWh). */
  balance: number;
  /** Créditos que vencem no próximo mês, quando a fatura informa (kWh). */
  expiringNextMonth: number;
}

export const creditBank = (
  units: ConsumerUnit[],
  invoices: Invoice[]
): CreditBankRow[] =>
  units
    .map((uc) => {
      const latest = [...byUnit(invoices, uc.id)].sort((a, b) =>
        a.refMonth < b.refMonth ? 1 : -1
      )[0];
      if (!latest) return null;
      const ce = latest.creditEnergy as
        | (Invoice['creditEnergy'] & { balanceP?: number; balanceInt?: number })
        | undefined;
      const balance = n(ce?.balanceFP) + n(ce?.balanceP) + n(ce?.balanceInt);
      return {
        unitId: uc.id,
        unitName: unitName(uc),
        refMonth: latest.refMonth,
        balance,
        expiringNextMonth: n(ce?.balanceToExpireFP),
      };
    })
    .filter((r): r is CreditBankRow => r !== null && r.balance > 0);

/** Saldo acima disto (em meses de consumo médio) = selo "alto" no card. */
export const HIGH_BALANCE_MONTHS = 3;

export interface UnitCreditBalance extends CreditBankRow {
  /** Consumo médio mensal da distribuidora nas últimas 12 faturas (kWh). */
  avgMonthlyKwh: number;
  /** Quantos meses de consumo médio o saldo cobre. */
  monthsCovered: number;
}

// A UC participa da compensação: tem FV ou alguma fatura trouxe saldo ou
// crédito usado (UC só receptora não tem `hasFv`)
const takesPartInGd = (uc: ConsumerUnit, invoices: Invoice[]) =>
  uc.hasFv ||
  invoices.some((inv) => {
    const ce = inv.creditEnergy as
      | (Invoice['creditEnergy'] & { balanceP?: number; balanceInt?: number })
      | undefined;
    return (
      n(ce?.balanceFP) + n(ce?.balanceP) + n(ce?.balanceInt) > 0 ||
      n(ce?.creditUsedTEFP) + n(ce?.creditUsedTUSDFP) > 0
    );
  });

/**
 * Saldo de créditos de UMA UC (fatura mais recente) + quantos meses de consumo
 * médio ele cobre — a linha "Créditos" do card de UC no Dashboard (out/2026).
 * Consumo médio = energia FORNECIDA (faturada) das últimas 12 faturas — é o
 * que o crédito abate. Saldo zero também aparece quando a UC participa da
 * compensação (26/out/2026 — a UC sem a linha parecia sem GD); `null` = UC
 * fora da compensação ou sem fatura.
 * O aviso de saldo alto no topo do Dashboard (`highCreditBalances`) saiu em
 * 26/out/2026 — decisão do Bruno; o selo "alto" do card ficou.
 */
export const unitCreditBalance = (
  uc: ConsumerUnit,
  invoices: Invoice[]
): UnitCreditBalance | null => {
  const recent = [...byUnit(invoices, uc.id)].sort((a, b) =>
    a.refMonth < b.refMonth ? 1 : -1
  );
  const latest = recent[0];
  if (!latest || !takesPartInGd(uc, recent)) return null;
  const ce = latest.creditEnergy as
    | (Invoice['creditEnergy'] & { balanceP?: number; balanceInt?: number })
    | undefined;
  const balance = n(ce?.balanceFP) + n(ce?.balanceP) + n(ce?.balanceInt);
  const kwh = recent
    .slice(0, 12)
    .map(periodConsumptionKwh)
    .filter((v) => v > 0);
  const avgMonthlyKwh = kwh.length
    ? kwh.reduce((a, b) => a + b, 0) / kwh.length
    : 0;
  return {
    unitId: uc.id,
    unitName: unitName(uc),
    refMonth: latest.refMonth,
    balance,
    expiringNextMonth: n(ce?.balanceToExpireFP),
    avgMonthlyKwh,
    monthsCovered: avgMonthlyKwh > 0 ? balance / avgMonthlyKwh : Infinity,
  };
};

// ── 6. Desempenho solar ───────────────────────────────────────────────────

export interface SolarPerformanceRow {
  unitId: string;
  unitName: string;
  generated: number;
  expected: number;
  /** Geração real ÷ esperada nos meses com os dois dados. */
  ratio: number | null;
  /** Meses do período sem geração do monitoramento. */
  monthsWithoutData: number;
}

export const solarPerformance = (
  units: ConsumerUnit[],
  months: string[]
): SolarPerformanceRow[] =>
  units
    .filter((uc) => uc.hasFv && (uc.plantList?.length ?? 0) > 0)
    .map((uc) => {
      let generated = 0;
      let expected = 0;
      let monthsWithoutData = 0;
      months.forEach((rm) => {
        const gen = uc.monthGenerationList?.find(
          (g) => g.refMonth === rm
        )?.value;
        const exp = uc.plantList.reduce(
          (a, p) =>
            a +
            n(
              p.plantExpectedGenerationList?.find(
                (e) => e.month === rm.slice(-2)
              )?.value
            ),
          0
        );
        if (gen === undefined || gen === null || gen <= 0) {
          monthsWithoutData += 1;
          return;
        }
        if (exp > 0) {
          generated += gen;
          expected += exp;
        }
      });
      return {
        unitId: uc.id,
        unitName: unitName(uc),
        generated,
        expected,
        ratio: expected > 0 ? generated / expected : null,
        monthsWithoutData,
      };
    });

// ── 7. Orçamento ──────────────────────────────────────────────────────────

export interface BudgetRow {
  unitId: string;
  unitName: string;
  budget: number | null;
  /** Média mensal realizada no período (R$). */
  avgRealized: number | null;
  /** Meses do período acima do orçamento. */
  monthsOver: number;
  months: number;
}

export const budgetRows = (
  units: ConsumerUnit[],
  invoices: Invoice[]
): BudgetRow[] =>
  units.map((uc) => {
    const list = byUnit(invoices, uc.id).filter((i) => n(i.invoiceValue) > 0);
    const budget = uc.monthlyBudget ?? null;
    const total = list.reduce((a, i) => a + n(i.invoiceValue), 0);
    return {
      unitId: uc.id,
      unitName: unitName(uc),
      budget,
      avgRealized: list.length ? total / list.length : null,
      monthsOver:
        budget !== null
          ? list.filter((i) => n(i.invoiceValue) > budget).length
          : 0,
      months: list.length,
    };
  });

// ── 8. Completude dos dados ───────────────────────────────────────────────

export interface CompletenessRow {
  unitId: string;
  unitName: string;
  missingMonths: string[];
  expectedMonths: number;
}

/**
 * Meses do período sem fatura, por imóvel. O último mês não conta (a conta
 * mais recente pode ainda não ter chegado — mesma regra do aviso do Dashboard)
 * nem os meses anteriores à 1ª fatura da UC (imóvel recém-cadastrado).
 */
export const dataCompleteness = (
  units: ConsumerUnit[],
  invoices: Invoice[],
  months: string[]
): CompletenessRow[] => {
  const required = months.slice(0, -1);
  return units.map((uc) => {
    const list = byUnit(invoices, uc.id);
    const first = list.reduce<string | null>(
      (min, i) => (min === null || i.refMonth < min ? i.refMonth : min),
      null
    );
    const expected = required.filter((rm) => first !== null && rm >= first);
    return {
      unitId: uc.id,
      unitName: unitName(uc),
      missingMonths: expected.filter(
        (rm) => !list.some((i) => i.refMonth === rm)
      ),
      expectedMonths: expected.length,
    };
  });
};
